import { useLazyQuery } from '@apollo/client';
import { useMergeLink } from '@mergeapi/react-merge-link';
import { useEffect, useState } from 'react';

import {
  GET_MERGE_DEV_LINK_TOKEN,
  GetMergeDevLinkTokenQuery,
  GetMergeDevLinkTokenQueryVariables,
  MergeDevProvider,
} from '@neo.tax/shared/graphql';

export interface MergeLinkTokenParams {
  userId: string;
  companyId: string;
  provider: MergeDevProvider;
  onSuccess: (publicToken: string) => void;
  onExit?: () => void;
}

export interface MergeLinkTokenObject {
  fetchLinkToken: () => void;
  linkToken: string | null;
  isReady: boolean;
  open: () => void;
  error: ErrorEvent | null;
}

/**
 * This method and the one below should only be used on one token at a time.
 * Only the last use will return isReady = true.
 */
export const useMergeLinkToken = ({
  companyId,
  onExit,
  onSuccess,
  provider,
  userId,
}: MergeLinkTokenParams): MergeLinkTokenObject => {
  const [fetchLinkToken, { data }] = useLazyQuery<GetMergeDevLinkTokenQuery, GetMergeDevLinkTokenQueryVariables>(
    GET_MERGE_DEV_LINK_TOKEN,
    {
      variables: { userId, input: { companyId, provider } },
      fetchPolicy: 'network-only',
    },
  );
  const mergeLinkToken = data?.getMergeDevLinkToken;
  const [linkToken, setLinkToken] = useState<string | null>(mergeLinkToken ?? null);

  useEffect(() => {
    if (mergeLinkToken) {
      setLinkToken(mergeLinkToken);
    }
  }, [mergeLinkToken]);

  const reset = (): void => {
    setLinkToken(null);
    if (onExit) {
      onExit();
    }
  };

  const { error, isReady, open } = useMergeLink({
    linkToken: linkToken || undefined,
    onSuccess,
    onExit: reset,
  });

  if (error) {
    throw error;
  }

  return { isReady, fetchLinkToken, linkToken, open, error };
};
